/* eslint-disable react-hooks/exhaustive-deps */
import { Box, Button, Card, CardContent, Chip, Grid } from "@mui/material";
import { Clear, LabelOff, LocalOffer, Search } from "@mui/icons-material";
import { GridRowModel, GridRowSelectionModel, GridSortDirection, GridSortModel } from "@mui/x-data-grid";
import { useEffect, useMemo, useState } from "react";

import AutoComplete from "@/components/hookForms/autoComplete";
import { Container } from "@/theme/globalStyles";
import DataTable from "@/components/dataTable";
import IGenericResult from "@/interfaces/IGenericResult";
import InputDocumento from "@/components/hookForms/inputDocumento";
import TextInput from "@/components/hookForms/textField";
import messages from "@/utils/messages";
import serviceConcessionaria from "@/services/serviceConcessionaria";
import serviceMarcador from "@/services/serviceMarcador";
import { toast } from "react-toastify";
import { useAuth } from "@/context/global";
import { useForm } from "react-hook-form";
import ConfirmationDialog from "@/components/confirmationDialog";
import DialogMarcador from "./modal/dialogMarcador";

// Opção do filtro "Marcador" que lista as instalações sem nenhum marcador.
const SEM_MARCADOR = -1;

const Marcadores = () => {
    const { setTitlePage } = useAuth();

    useEffect(() => {
        setTitlePage("Cadastro - Marcadores");
    }, [setTitlePage]);

    const [data, setData] = useState<any[]>([]);
    const [pageSize, setPageSize] = useState(100);
    const [page, setPage] = useState(0);
    const [filters, setFilters] = useState<any>(null);
    const [rowCount, setRowCount] = useState(0);
    const [isLoading, setLoading] = useState(false);
    const [linesSelecteds, setLinesSelecteds] = useState<GridRowSelectionModel>([]);
    const [marcadorOptions, setMarcadorOptions] = useState<IGenericResult[]>([]);
    const [concessionariaDataOptions, setConcessionariaDataOptions] = useState<IGenericResult[]>([]);
    const [atribuirOpen, setAtribuirOpen] = useState(false);
    const [removerOpen, setRemoverOpen] = useState(false);
    const [removerLoading, setRemoverLoading] = useState(false);

    // Instalações selecionadas na página atual, com o marcador atual de cada uma.
    const instalacoesSelecionadas = useMemo(
        () =>
            (data ?? [])
                .filter((row) => linesSelecteds.includes(row.clienteInstalacaoID))
                .map((row) => ({ clienteInstalacaoID: row.clienteInstalacaoID, marcadorID: row.marcadorID ?? null })),
        [data, linesSelecteds]
    );

    const [sortModel, setSortModel] = useState<GridSortModel>([
        {
            field: "",
            sort: "" as GridSortDirection,
        },
    ]);

    const {
        control,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm({
        defaultValues: {
            nomeCliente: "",
            numeroDocumentoCliente: "",
            numeroInstalacao: "",
            concessionariaID: "",
            marcadorID: "",
            flagHabilitado: "Sim",
        },
    });

    const filtersOptions = [
        { label: "Todos", value: "Todos" },
        { label: "Sim", value: "Sim" },
        { label: "Não", value: "Não" },
    ];

    const marcadorFilterOptions = useMemo(
        () => [...marcadorOptions, { label: "Sem marcador", value: SEM_MARCADOR }],
        [marcadorOptions]
    );

    const columns = useMemo(
        () => [
            {
                headerName: "ID",
                field: "clienteInstalacaoID",
                type: "number",
                headerAlign: "left",
                minWidth: 100,
                flex: 1,
            },
            {
                headerName: "Cliente",
                field: "nomeCliente",
                type: "string",
                minWidth: 300,
                flex: 1,
            },
            {
                headerName: "CPF/CNPJ",
                field: "numeroDocumentoClienteFormatado",
                type: "string",
                minWidth: 150,
                flex: 1,
            },
            {
                headerName: "Instalação",
                field: "numeroInstalacao",
                headerAlign: "left",
                type: "string",
                minWidth: 150,
                flex: 1,
            },
            {
                headerName: "Concessionaria",
                field: "nomeConcessionaria",
                type: "string",
                minWidth: 250,
                flex: 1,
            },
            {
                headerName: "Parceiro",
                field: "nomeParceiro",
                type: "string",
                minWidth: 150,
                flex: 1,
            },
            {
                headerName: "Habilitado",
                field: "flagHabilitado",
                type: "string",
                minWidth: 100,
                flex: 1,
            },
            {
                headerName: "Marcador",
                field: "nomeMarcador",
                type: "string",
                minWidth: 200,
                flex: 1,
                sortable: false,
                renderCell: (params: any) =>
                    params.row.nomeMarcador ? (
                        <Chip label={params.row.nomeMarcador} size="small" color="primary" variant="outlined" />
                    ) : null,
            },
        ],
        []
    );

    useEffect(() => {
        setMarcadorData();
        setConcessionariaData();
    }, []);

    const setMarcadorData = async () => {
        serviceMarcador
            .getAll()
            .then((result) => {
                setMarcadorOptions(
                    (result?.data ?? []).map((item: any) => ({ value: item.marcadorID, label: item.nomeMarcador }))
                );
            })
            .catch(() => {
                setMarcadorOptions([]);
            });
    };

    const setConcessionariaData = async () => {
        serviceConcessionaria
            .getConcessionarias()
            .then((result) => {
                setConcessionariaDataOptions(result?.data);
            })
            .catch(() => {
                setConcessionariaDataOptions([]);
            });
    };

    const getAllWithFilters = async (filters: any) => {
        setData([]);
        setLoading(true);

        if (!filters) {
            filters = { flagHabilitado: "Sim" };
        }

        serviceMarcador
            .getWithFilter({
                ...filters,
                take: pageSize,
                currentPage: page + 1,
                sortExpression: sortModel[0]?.field ?? "",
                sortDirection: sortModel[0]?.sort ?? "",
            })
            .then((result) => {
                setData(result?.data.content);
                setRowCount(result?.data.numberOfElements);
            })
            .catch((err) => {
                toast.error(messages.getWithFilterError, err);
                setData([]);
                setRowCount(0);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    useEffect(() => {
        getAllWithFilters(filters);
    }, [page, pageSize, sortModel]);

    const onSubmit = (values: any) => {
        const semMarcador = Number(values.marcadorID) === SEM_MARCADOR;
        const newFilters = {
            ...values,
            marcadorID: semMarcador ? "" : values.marcadorID,
            semMarcador: semMarcador ? "Sim" : "",
            flagHabilitado: values.flagHabilitado === "Todos" ? "" : values.flagHabilitado,
        };
        setLinesSelecteds([]);
        setFilters(newFilters);
        getAllWithFilters(newFilters);
    };

    const onClear = () => {
        reset({
            nomeCliente: "",
            numeroDocumentoCliente: "",
            numeroInstalacao: "",
            concessionariaID: "",
            marcadorID: "",
            flagHabilitado: "Sim",
        });
        setLinesSelecteds([]);
        setFilters(null);
        getAllWithFilters(null);
    };

    const onSuccessAction = () => {
        setLinesSelecteds([]);
        getAllWithFilters(filters);
    };

    // Cada instalação tem no máximo 1 marcador: remove o que houver, sem escolher qual.
    const onConfirmRemover = () => {
        setRemoverLoading(true);
        serviceMarcador
            .remover({ clienteInstalacaoIDs: instalacoesSelecionadas.map((i) => i.clienteInstalacaoID) })
            .then((result) => {
                if (result) {
                    toast.success(result.data?.mensagem || messages.updateSucess);
                    onSuccessAction();
                }
            })
            .catch((err) => {
                toast.error(err?.response?.data?.mensagem || messages.updateError);
            })
            .finally(() => {
                setRemoverLoading(false);
                setRemoverOpen(false);
            });
    };

    return (
        <Container>
            <Card>
                <CardContent>
                    <Box sx={{ mb: 2 }}>
                        <form onSubmit={handleSubmit(onSubmit)}>
                            <Grid container spacing={2} display="flex" alignItems="center">
                                <Grid item xs={12} md={2}>
                                    <TextInput
                                        id="nomeCliente"
                                        name="nomeCliente"
                                        label="Nome cliente"
                                        control={control}
                                        error={errors}
                                        maxLength={200}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <InputDocumento
                                        id="numeroDocumentoCliente"
                                        name="numeroDocumentoCliente"
                                        label="Documento"
                                        control={control}
                                        error={errors}
                                        maxLength={20}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <AutoComplete
                                        id="concessionariaID"
                                        name="concessionariaID"
                                        label="Concessionaria"
                                        options={concessionariaDataOptions}
                                        control={control}
                                        error={errors}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <AutoComplete
                                        id="flagHabilitado"
                                        name="flagHabilitado"
                                        label="Habilitado"
                                        options={filtersOptions}
                                        control={control}
                                        error={errors}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <Button
                                        id="pesquisar"
                                        style={{ marginTop: 16, marginBottom: 8 }}
                                        variant="contained"
                                        color="warning"
                                        startIcon={<Search />}
                                        disableElevation
                                        type="submit"
                                        disabled={isLoading}
                                        fullWidth
                                        size="medium"
                                    >
                                        Pesquisar
                                    </Button>
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <Button
                                        id="limpar"
                                        style={{ marginTop: 16, marginBottom: 8 }}
                                        variant="outlined"
                                        color="warning"
                                        startIcon={<Clear />}
                                        onClick={() => onClear()}
                                        disableElevation
                                        fullWidth
                                        size="medium"
                                    >
                                        Limpar
                                    </Button>
                                </Grid>
                            </Grid>
                            <Grid container spacing={2} display="flex" alignItems="center">
                                <Grid item xs={12} md={4}>
                                    <TextInput
                                        id="numeroInstalacao"
                                        name="numeroInstalacao"
                                        label="Instalação(ões) - separe por vírgula para pesquisar várias"
                                        control={control}
                                        error={errors}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <AutoComplete
                                        id="marcadorID"
                                        name="marcadorID"
                                        label="Marcador"
                                        options={marcadorFilterOptions}
                                        control={control}
                                        error={errors}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2} />
                                <Grid item xs={12} md={2}>
                                    <Button
                                        id="atribuirMarcador"
                                        style={{ marginTop: 16, marginBottom: 8 }}
                                        variant="contained"
                                        color="primary"
                                        startIcon={<LocalOffer />}
                                        onClick={() => setAtribuirOpen(true)}
                                        disableElevation
                                        disabled={isLoading || linesSelecteds.length <= 0}
                                        fullWidth
                                        size="medium"
                                    >
                                        Atribuir
                                    </Button>
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <Button
                                        id="removerMarcador"
                                        style={{ marginTop: 16, marginBottom: 8 }}
                                        variant="outlined"
                                        color="primary"
                                        startIcon={<LabelOff />}
                                        onClick={() => setRemoverOpen(true)}
                                        disableElevation
                                        disabled={isLoading || linesSelecteds.length <= 0}
                                        fullWidth
                                        size="medium"
                                    >
                                        Remover
                                    </Button>
                                </Grid>
                            </Grid>
                        </form>
                    </Box>

                    <Box sx={{ height: 450 }}>
                        <DataTable
                            columns={columns}
                            rows={data}
                            rowId={(row: GridRowModel) => row.clienteInstalacaoID}
                            rowCount={rowCount}
                            isLoading={isLoading}
                            onPageChange={(currentPage: number) => setPage(currentPage)}
                            onPageSizeChange={(currentPageSize: number) => setPageSize(currentPageSize)}
                            pageSize={pageSize}
                            onSortModelChange={(model: GridSortModel) => setSortModel(model)}
                            checkboxSelection
                            disableRowSelectionOnClick
                            onRowSelectionModelChange={(newSelectionModel: GridRowSelectionModel) => {
                                setLinesSelecteds(newSelectionModel);
                            }}
                            rowSelectionModel={linesSelecteds}
                        />
                    </Box>

                    <DialogMarcador
                        open={atribuirOpen}
                        marcadorOptions={marcadorOptions}
                        instalacoes={instalacoesSelecionadas}
                        onClose={() => setAtribuirOpen(false)}
                        onSuccess={() => onSuccessAction()}
                    />

                    <ConfirmationDialog
                        open={removerOpen}
                        loading={removerLoading}
                        title={"Remover Marcador"}
                        message={`Deseja realmente remover o marcador de ${instalacoesSelecionadas.length} instalação(ões) selecionada(s)?`}
                        cancelButtonText={"Não"}
                        confirmButtonText={"Sim"}
                        onClose={() => setRemoverOpen(false)}
                        onCancel={() => setRemoverOpen(false)}
                        onConfirm={onConfirmRemover}
                    />
                </CardContent>
            </Card>
        </Container>
    );
};

export default Marcadores;
